import React from 'react';
import useGameStore from '../../store/gameStore';
import { formatMoney } from '../../utils/format';
import { XCircle, AlertTriangle, X } from 'lucide-react';
import { useTranslation } from '../../i18n';

/**
 * Confirmation modal shown before resiliating an active contract.
 * Penalty = 25 % of remaining gross revenue + reputation drop of 5 points.
 */
export const CancelContractModal = ({ contract, onClose }) => {
  const { t } = useTranslation();
  const cancelContract = useGameStore(s => s.cancelContract);
  const treasury = useGameStore(s => s.treasury);

  const remainingRevenue = (contract.daysRemaining || 0) * (contract.dailyRevenue || 0);
  const penalty = Math.round(remainingRevenue * 0.25);
  const projectedTreasury = treasury - penalty;

  const handleConfirm = () => {
    cancelContract(contract.instanceId);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="cancel-contract-overlay"
    >
      <div
        className="bg-ink-800 rounded-xl w-full max-w-md shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200"
        data-testid="cancel-contract-modal"
      >
        <div className="bg-danger/90 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <XCircle className="w-5 h-5" />
            <h3 className="font-bold text-base">{t('cancel_contract_title')}</h3>
          </div>
          <button aria-label="Annuler" onClick={onClose} className="text-white/70 hover:text-white" data-testid="close-cancel-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-300 font-semibold">{contract.name}</p>

          <div className="rounded-md border border-warn/40 bg-warn/5 px-3 py-2 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-warn flex-shrink-0 mt-0.5" />
            <p className="text-[12px] text-warn leading-snug">{t('cancel_contract_warning')}</p>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-md border border-ink-600 bg-ink-700/30 p-2.5">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('cancel_contract_remaining_revenue')}</p>
              <p className="text-base font-bold text-slate-200 mt-0.5" data-testid="remaining-revenue">{formatMoney(remainingRevenue)}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">{contract.daysRemaining}j × {formatMoney(contract.dailyRevenue)}</p>
            </div>
            <div className="rounded-md border border-danger/40 bg-danger/5 p-2.5">
              <p className="text-[10px] uppercase tracking-wider text-danger">{t('cancel_contract_penalty')}</p>
              <p className="text-base font-bold text-danger mt-0.5" data-testid="cancellation-penalty">{formatMoney(penalty)}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">{t('cancel_contract_rep_loss')} : -5</p>
            </div>
          </div>

          <div className="rounded-md border border-ink-600 bg-ink-700/30 p-2.5 text-xs">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('treasury')} (post-résiliation)</p>
            <p className={`text-base font-bold mt-0.5 ${projectedTreasury < 0 ? 'text-danger' : 'text-money'}`} data-testid="projected-treasury">
              {formatMoney(projectedTreasury)}
            </p>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-ink-600 bg-ink-700/30 flex items-center justify-between">
          <button
            onClick={onClose}
            className="text-xs text-slate-400 hover:text-slate-200 px-3 py-2 transition-colors"
            data-testid="keep-contract-btn"
          >
            {t('keep_contract')}
          </button>
          <button
            onClick={handleConfirm}
            className="bg-danger text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-red-700 transition-colors flex items-center gap-2"
            data-testid="confirm-cancel-contract-btn"
          >
            <XCircle className="w-4 h-4" />
            {t('cancel_contract_confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelContractModal;
